import React, { useEffect, useState } from 'react';
import { CircularProgress, Modal } from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import EntityServiceAPI from 'api/EntityServiceAPI';
import {
  downloadEntityChangesCsv,
  downloadEntityChangesPdf,
  EntityChangeDetail,
  getReportableEntityChanges,
} from 'utils/entityChangesDocument';

interface IEntityChangesReportModal {
  open: boolean;
  onClose: () => void;
  databaseId: string;
  databaseName: string;
  /** Selected DatabaseEntity ids. When non-empty, the report is scoped to these
   *  entities and includes per-hit detail (PDF/CSV). Empty = whole-database CSV. */
  selectedEntityIds?: string[];
}

type DetailFormat = 'PDF' | 'CSV';

// Mirrors the date-input styling used by Screening/filters/DateSelector.
const datePickerSx = {
  flex: 1,
  '& .MuiInputBase-root': {
    height: '44px',
    borderRadius: '8px',
    background: 'var(--bg-colors)',
    border: '1px solid var(--border-color)',
    color: 'var(--text-primary-color)',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 500,
  },
  '& input': {
    color: 'var(--text-primary-color)',
    fontSize: '14px',
    fontWeight: 500,
  },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiSvgIcon-root': { color: 'var(--text-secondary)' },
};

/**
 * Export screening changes over a date range. Two modes:
 *  - no selection  -> whole-database summary CSV, streamed from the entity service.
 *  - selection     -> per-entity detail (new / updated-fields / removed hits) for
 *                     the selected entities, as PDF or CSV, rendered client-side
 *                     from JSON the entity service returns.
 */
const EntityChangesReportModal: React.FC<IEntityChangesReportModal> = ({
  open,
  onClose,
  databaseId,
  databaseName,
  selectedEntityIds = [],
}) => {
  const isDetailMode = selectedEntityIds.length > 0;

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [fileName, setFileName] = useState('');
  const [detailFormat, setDetailFormat] = useState<DetailFormat>('PDF');
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFromDate(null);
      setToDate(null);
      setError(null);
      setIsDownloading(false);
      setDetailFormat('PDF');
      const today = format(new Date(), 'yyyy-MM-dd');
      setFileName(
        isDetailMode
          ? `${databaseName}_selected_changes_${today}`
          : `${databaseName}_changes_${today}`,
      );
    }
  }, [open, databaseName, isDetailMode]);

  const downloadWholeDatabaseCsv = async (from: string, to: string, name: string) => {
    const res = await EntityServiceAPI.exportEntityChanges({
      databaseId: databaseId,
      from: from,
      to: to,
    });
    if (!res || !res.success || !(res.result instanceof Blob)) {
      setError(
        typeof res?.result === 'string' && res.result
          ? res.result
          : 'Export failed. Please try again.',
      );
      return false;
    }
    const url = URL.createObjectURL(res.result);
    const link = document.createElement('a');
    link.href = url;
    link.download = name.toLowerCase().endsWith('.csv') ? name : `${name}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return true;
  };

  const downloadSelectedEntityDetail = async (from: string, to: string, name: string) => {
    const res = await EntityServiceAPI.getEntityChanges({
      databaseId: databaseId,
      databaseEntityIds: selectedEntityIds,
      from: from,
      to: to,
    });
    if (!res || !res.success || !res.result || !Array.isArray(res.result.entities)) {
      setError(
        typeof res?.result === 'string' && res.result
          ? res.result
          : 'Export failed. Please try again.',
      );
      return false;
    }
    const entities = getReportableEntityChanges(
      res.result.entities as EntityChangeDetail[],
    );
    if (entities.length === 0) {
      setError(
        'No added, updated, or removed screening hits were found for the selected entities in this range.',
      );
      return false;
    }
    const meta = { databaseName: databaseName, from: from, to: to };
    if (detailFormat === 'PDF') {
      downloadEntityChangesPdf(entities, meta, name);
    } else {
      downloadEntityChangesCsv(entities, meta, name);
    }
    return true;
  };

  const handleDownload = async () => {
    if (isDownloading) return;

    if (!fromDate || !toDate) {
      setError('Please select both a start and end date.');
      return;
    }
    if (fromDate > toDate) {
      setError('The "from" date must be on or before the "to" date.');
      return;
    }

    setError(null);
    setIsDownloading(true);

    const from = format(fromDate, 'yyyy-MM-dd');
    const to = format(toDate, 'yyyy-MM-dd');
    const name = fileName.trim() || `${databaseName}_changes_${from}_${to}`;

    try {
      const ok = isDetailMode
        ? await downloadSelectedEntityDetail(from, to, name)
        : await downloadWholeDatabaseCsv(from, to, name);
      if (ok) onClose();
    } catch (e) {
      setError('Export failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const subtitle = isDetailMode
    ? `${selectedEntityIds.length} selected ${
        selectedEntityIds.length === 1 ? 'entity' : 'entities'
      } — new, updated & removed hits.`
    : 'Download a CSV of entities whose screening results changed over a date range.';

  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ outline: 'none' }}>
        <div className='audit-add-modal-cont'>
          <div className='audit-add-modal-cont-head'>
            <div className='audit-add-modal-head-cont-change'>
              <div className='audit-add-modal-head-cont-icon'>
                <CloudDownloadOutlinedIcon
                  sx={{ color: '#4895EF', width: '25px', height: '24px' }}
                />
              </div>
              <div className='audit-add-modal-head-cont-supporting'>
                <h1>Export Entities with Changes</h1>
                <p>{subtitle}</p>
              </div>
            </div>
            <div
              className='audit-add-modal-head-close'
              onClick={onClose}
              style={{ cursor: 'pointer' }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
              >
                <path
                  d='M18 6L6 18M6 6L18 18'
                  stroke='#85888E'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
          </div>
          <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>

          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            {/* Filename */}
            <div className='audit-add-modal-cont-body-frame'>
              <div className='audit-add-modal-cont-body-frame-form'>
                <div className='audit-add-modal-cont-body-frame-form-row'>
                  <div className='audit-add-modal-cont-body-frame-form-row-input'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                      <input
                        className='audit-add-modal-cont-body-frame-form-input'
                        type='text'
                        placeholder='e.g. Acme_changes_2026-06'
                        value={fileName}
                        onChange={(e) => setFileName(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Date range */}
            <div className='audit-add-modal-cont-body-frame'>
              <div className='audit-add-modal-cont-body-frame-form'>
                <div className='audit-add-modal-cont-body-frame-form-row'>
                  <div className='audit-add-modal-cont-body-frame-form-row-input'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                      <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                        Date Range
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          width: '100%',
                        }}
                      >
                        <DatePicker<Date>
                          value={fromDate}
                          onChange={(newValue) => setFromDate(newValue)}
                          shouldDisableDate={(date) => date > new Date()}
                          slotProps={{ actionBar: { actions: ['clear'] } }}
                          sx={datePickerSx}
                        />
                        <span
                          style={{ color: 'var(--text-secondary)', fontSize: '14px' }}
                        >
                          –
                        </span>
                        <DatePicker<Date>
                          value={toDate}
                          onChange={(newValue) => setToDate(newValue)}
                          shouldDisableDate={(date) => date > new Date()}
                          slotProps={{ actionBar: { actions: ['clear'] } }}
                          sx={datePickerSx}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Format selector (selected-entities detail only) */}
            {isDetailMode && (
              <div className='audit-add-modal-cont-body-frame'>
                <div className='audit-add-modal-cont-body-frame-form'>
                  <div className='audit-add-modal-cont-body-frame-form-row'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input'>
                      <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                        <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                          Format
                        </div>
                        <div className='bill-container-fit-screen-content-row-selection'>
                          {(['PDF', 'CSV'] as DetailFormat[]).map((fmt) => (
                            <div
                              key={fmt}
                              className='bill-container-fit-screen-content-row-checkbox'
                              onClick={() => setDetailFormat(fmt)}
                            >
                              <div className='bill-container-fit-screen-content-row-input'>
                                <div
                                  className={`bill-container-fit-screen-content-row-input-icon ${
                                    detailFormat === fmt ? 'selected' : ''
                                  }`}
                                >
                                  <div
                                    className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                      detailFormat === fmt ? 'selected' : ''
                                    }`}
                                  />
                                </div>
                              </div>
                              <div className='bill-container-fit-screen-content-row-checkbox-text'>
                                <p>{fmt === 'PDF' ? 'PDF report' : 'CSV (per hit)'}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div
                style={{
                  color: 'var(--error-color, #F04438)',
                  fontSize: '13px',
                  marginTop: '4px',
                }}
              >
                {error}
              </div>
            )}
          </div>
          <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>

          {/* Footer */}
          <div className='audit-modal-cont-footer'>
            <div className='audit-modal-cont-footer-content'>
              <div
                className='audit-modal-cont-footer-content-btn-cancel'
                onClick={onClose}
                style={{ cursor: 'pointer' }}
              >
                <div className='audit-modal-cont-footer-content-btn-text'>Cancel</div>
              </div>
              <div
                className='audit-modal-cont-footer-content-btn-upload'
                onClick={handleDownload}
                style={{
                  cursor: isDownloading ? 'not-allowed' : 'pointer',
                  ...(isDownloading ? { opacity: 0.7 } : {}),
                }}
              >
                <div
                  className='audit-modal-cont-footer-content-btn-text'
                  style={{ color: '#fff', display: 'flex', alignItems: 'center' }}
                >
                  {isDownloading && (
                    <CircularProgress
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '10px',
                        color: '#fff',
                      }}
                    />
                  )}
                  Download
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EntityChangesReportModal;
